/**
 * Centralized API Configuration
 * 
 * Environment options:
 * - 'local': localhost:5000 (for local development)
 * - 'server': Amazon EC2 (for testing on the server)
 * - 'production': rsapipeline.click (for production)
 * 
 * Usage in components:
 * import { baseApi, API_BASE_URL, ENVIRONMENT } from '../API/api';
 * 
 * Example:
 * const response = await axios.post(`${baseApi}/api/endpoint`, data);
 */
// Environment configuration
// For local development, change this value manually — deploy tools override it automatically
const DEV_ENVIRONMENT = 'local'; // Options: 'local', 'dev', 'production'

// Deploy tools set REACT_APP_DEPLOY_TARGET at build time (production or dev).
// When set, it takes priority. Otherwise fall back to DEV_ENVIRONMENT for local dev.
const ENVIRONMENT = process.env.REACT_APP_DEPLOY_TARGET || DEV_ENVIRONMENT;

const PS_SOURCE_URL = 'production'; // Options: 'local' (manual URL entry) or 'server' (fetch from AWS Secrets Manager)

// Local Development URLs (localhost)
const LOCAL_APIS = {
  baseApi: 'http://localhost:5000',
  serviceApi: 'http://localhost:5005',
  dividerApi: 'http://localhost:5000',
  carRemovalApi: 'http://localhost:5005',
  horizontalApi: 'http://localhost:5005',
  ManagerlApi: 'http://localhost:5000',
  EmbankmentReportApi: 'http://localhost:5005',
  processApi: 'http://localhost:5002'
};

// Dev Server URLs (dev123bimlive.com)
const DEV_APIS = {
  baseApi: 'https://www.dev123bimlive.com',
  serviceApi: 'https://www.dev123bimlive.com',
  dividerApi: 'https://www.dev123bimlive.com',
  carRemovalApi: 'https://www.dev123bimlive.com',
  horizontalApi: 'https://www.dev123bimlive.com',
  ManagerlApi: 'https://www.dev123bimlive.com',
  EmbankmentReportApi: 'https://www.dev123bimlive.com',
  processApi: 'https://www.dev123bimlive.com'
};

// Production URLs (123bimlive.com - unified domain for all services)
const PRODUCTION_APIS = {
  baseApi: 'https://www.123bimlive.com',
  serviceApi: 'https://www.123bimlive.com',
  dividerApi: 'https://www.123bimlive.com',
  carRemovalApi: 'https://www.123bimlive.com',
  horizontalApi: 'https://www.123bimlive.com',
  ManagerlApi: 'https://www.123bimlive.com',  // Manager service runs through CloudFront
  EmbankmentReportApi: 'https://www.123bimlive.com',
  processApi: 'https://www.123bimlive.com'  // Process service runs through CloudFront CDN /rsa/* behavior
};

// Select APIs based on environment
let APIS;
switch (ENVIRONMENT) {
  case 'local':
    APIS = LOCAL_APIS;
    break;
  case 'dev':
    APIS = DEV_APIS;
    break;
  case 'production':
    APIS = PRODUCTION_APIS;
    break;
  default:
    console.warn(`Unknown environment: ${ENVIRONMENT}, defaulting to local`);
    APIS = LOCAL_APIS;
}

// Same-origin override for deployed stacks.
//
// Each CloudFront distribution serves the frontend on BOTH the apex and the
// `www.` alias, but the constants above are pinned to `www.`. A user who loads
// https://dev123bimlive.com then calls https://www.dev123bimlive.com/api/... —
// a different host, so the browser sends a CORS preflight, and the backend
// rejected it (it advertised Access-Control-Allow-Origin: http://localhost:3000),
// breaking calls like /api/admin/get-username-by-email.
//
// /api/* and /rsa/* are CloudFront behaviours on every alias, so simply calling
// the origin the user actually loaded is same-origin: no preflight, no CORS.
if (typeof window !== 'undefined' && (ENVIRONMENT === 'dev' || ENVIRONMENT === 'production')) {
  const host = window.location.hostname;
  const apex = ENVIRONMENT === 'dev' ? 'dev123bimlive.com' : '123bimlive.com';
  // Exact matches only — 'dev123bimlive.com'.endsWith('123bimlive.com') is true,
  // so a suffix test would wrongly rewrite dev hosts under the production build.
  if (host === apex || host === `www.${apex}`) {
    const origin = window.location.origin;
    APIS = Object.keys(APIS).reduce((acc, key) => ({ ...acc, [key]: origin }), {});
  }
}

// Optional per-service override for HYBRID local dev: point ONLY the
// pipeline at another stack (e.g. DEV) to see real run/Redis data while
// everything else stays on the local backends. Set at npm-start time:
//   $env:REACT_APP_PROCESS_API='http://ec2-13-216-36-99.compute-1.amazonaws.com:5002'; npm start
// (run_local_devdata.ps1 does exactly this). Use the Pipeline EC2 origin, NOT
// https://www.dev123bimlive.com — under ENVIRONMENT 'local' the app builds
// local-style paths (/process, /api/progress/...), which CloudFront routes to
// the S3 frontend and the 123bimlive EC2 — the wrong origins. The Pipeline EC2
// serves the same routes as localhost:5002. Unset = no effect.
if (process.env.REACT_APP_PROCESS_API) {
  APIS = { ...APIS, processApi: process.env.REACT_APP_PROCESS_API };
}

// Export individual APIs
export const baseApi = APIS.baseApi;
export const serviceApi = APIS.serviceApi;
export const dividerApi = APIS.dividerApi;
export const carRemovalApi = APIS.carRemovalApi;
export const horizontalApi = APIS.horizontalApi;
export const ManagerlApi = APIS.ManagerlApi;
export const EmbankmentReportApi = APIS.EmbankmentReportApi;
export const processApi = APIS.processApi;

// For relative URL requests, we need to use the baseApi as the base URL
// This ensures that relative URLs like '/api/projects' resolve to the correct backend
export const API_BASE_URL = baseApi;

// Debug logging
console.log('🔧 API Configuration Debug:');
console.log('🔧 ENVIRONMENT:', ENVIRONMENT);
console.log('🔧 APIS object:', APIS);
console.log('🔧 baseApi:', baseApi);
console.log('🔧 API_BASE_URL:', API_BASE_URL);
console.log('🔧 LOCAL_APIS.baseApi:', LOCAL_APIS.baseApi);
console.log('🔧 PRODUCTION_APIS.baseApi:', PRODUCTION_APIS.baseApi);

// Google Maps API key (set in .env as REACT_APP_GOOGLE_MAPS_API_KEY)
export const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

// Export the environment flag for reference
export { ENVIRONMENT, PS_SOURCE_URL };
